/**
 * Capture-phase keydown dispatcher.
 *
 * Returns an attach/detach pair so the player can register/unregister
 * the listener cleanly via destroy().
 */
import { hijack } from './utils.js';
import { frameStep } from './frame-step.js';

/**
 * @param {Object} ctx
 * @param {HTMLVideoElement} ctx.video
 * @param {HTMLElement}      ctx.stage
 * @param {() => void}       ctx.handleB           B-key — set marker / convert region
 * @param {() => void}       ctx.handleSpace       Space-K — play/pause OR loop control
 * @param {() => void}       [ctx.handleHelp]      ? — toggle help overlay
 * @param {() => void}       [ctx.handlePip]       P — toggle picture-in-picture
 * @param {() => void}       [ctx.handleDebug]     D — toggle zapd-engine debug overlay
 * @param {() => void}       ctx.renderStrip
 * @param {() => Object}     ctx.editorSettings    fresh-read each call (so changes take effect)
 * @param {() => number}     ctx.fps
 * @param {() => Object}     [ctx.engineBridge]    optional — zapd-engine bridge with frameStep(N)
 * @param {{ zoomLevel: number, panOffsetT: number }} ctx.timelineState
 */
export function attachKeyboard(ctx) {
  const handler = (e) => {
    if (e.target.matches?.('input, select, textarea, [contenteditable]')) return;
    const k = (e.key || '').toLowerCase();
    const { video, stage } = ctx;

    if (k === '?') { hijack(e); ctx.handleHelp?.(); return; }

    if (k === 'b') { hijack(e); ctx.handleB(); return; }

    if (k === ' ' || k === 'spacebar' || k === 'k') { hijack(e); ctx.handleSpace(); return; }

    if (k === 'p') { hijack(e); ctx.handlePip?.(); return; }
    if (k === 'd') { hijack(e); ctx.handleDebug?.(); return; }

    if (e.shiftKey && (k === 'arrowright' || k === 'arrowleft')) {
      hijack(e);
      const settings = ctx.editorSettings();
      frameStep(video, ctx.fps(), settings.frameStep, k === 'arrowright' ? 1 : -1, ctx.engineBridge?.());
      return;
    }

    if (k === 'arrowright' || k === 'l') {
      hijack(e);
      const s = ctx.editorSettings();
      const step = video.paused ? s.arrowPausedStep : s.arrowPlayingStep;
      video.currentTime = Math.min(video.duration || 1e9, video.currentTime + step);
      return;
    }
    if (k === 'arrowleft' || k === 'j') {
      hijack(e);
      const s = ctx.editorSettings();
      const step = video.paused ? s.arrowPausedStep : s.arrowPlayingStep;
      video.currentTime = Math.max(0, video.currentTime - step);
      return;
    }

    if (k === '+' || k === '=') {
      hijack(e);
      ctx.timelineState.zoomLevel = Math.min(200, (ctx.timelineState.zoomLevel || 1) * 1.5);
      ctx.renderStrip(); return;
    }
    if (k === '-' || k === '_') {
      hijack(e);
      ctx.timelineState.zoomLevel = Math.max(1, (ctx.timelineState.zoomLevel || 1) / 1.5);
      ctx.renderStrip(); return;
    }

    if (k === 'f') {
      hijack(e);
      document.fullscreenElement ? document.exitFullscreen() : stage.requestFullscreen?.();
      return;
    }
    // Plan v2 (2026-05-02): E togglet den Editor-Strip (default hidden).
    // F bleibt Fullscreen, damit User-Memory intakt bleibt.
    if (k === 'e') {
      hijack(e);
      try { ctx.handleEditor?.(); } catch {}
      return;
    }
    if (k === 'm')         { hijack(e); video.muted = !video.muted; return; }
    if (k === 'arrowup')   { hijack(e); video.volume = Math.min(1, video.volume + 0.05); return; }
    if (k === 'arrowdown') { hijack(e); video.volume = Math.max(0, video.volume - 0.05); return; }
    // 2026-05-08: comma/period = frame-step (Netflix/YouTube convention).
    // Shift+comma/period = 5-frame step. Replaces previous playback-rate
    // bindings — speed-control is in the gear-menu instead, which is the
    // canonical 2026 video-player UX. < / > are still rate (= keyboard
    // without shift on US-layout) for backward-compat with power-users.
    if (k === ',') {
      hijack(e);
      const stepCount = e.shiftKey ? 5 : 1;
      frameStep(video, ctx.fps(), stepCount, -1, ctx.engineBridge?.());
      return;
    }
    if (k === '.') {
      hijack(e);
      const stepCount = e.shiftKey ? 5 : 1;
      frameStep(video, ctx.fps(), stepCount, +1, ctx.engineBridge?.());
      return;
    }
    if (k === '<') { hijack(e); video.playbackRate = Math.max(0.25, video.playbackRate - 0.25); return; }
    if (k === '>') { hijack(e); video.playbackRate = Math.min(3.0,  video.playbackRate + 0.25); return; }
    if (/^[0-9]$/.test(k)) { hijack(e); video.currentTime = (video.duration || 0) * (parseInt(k) / 10); return; }
  };

  window.addEventListener('keydown', handler, true);
  return () => window.removeEventListener('keydown', handler, true);
}
